import { IFiDialogResult } from '../shared/fi-dialogs/fi-dialogs';
import { AccessRightFilterCollectionModel, IAccessRightFilterCollection } from '../shared/models';
import { ILoadDataArgs } from '../shared/fi-items-list/fi-data-source-base.directive';
import { ITaskParameter } from '../shared/fi-task-shared/fi-task-shared';
import { ITabItem } from '../utilities/tabs/tabs';
import { FiStateNames } from '../shared/fi-state-proxy/fi-state-names';
import { FiDialogsService } from '../shared/fi-dialogs';
import { FiSnackBarService } from '../shared/fi-snackbar/snack-bar.service';
import { FiUserDataService } from '../shared/fi-shared-services/fi-user-data.service';
import { AppTextService } from '../shared/app-text';
import { StateAccessCheckService } from '../shared/fi-shared-services/state-access-check.service';
import { FiTaskCategories, FiTaskPriorities } from './fi-task-v2-consts';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AccessToApplicationTypeService } from '../shared/fi-shared-services/access-to-application-type.service';
import { AccessRightType, ApplicationSettingsType, SideListOptionType } from '../shared/types';
import { FiTaskAssignmentType, FiTaskCategoryType, FiTaskPriorityType, FiTaskType } from './fi-task-v2-enums';
import { FiTasksViewMode } from '../shared/fi-task-shared/fi-task-enums';
import { ITaskV2, ITaskV2Base, ITaskV2ChecklistDataBase } from './fi-task-v2';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiDateTimeFormatOptions, isDefined } from '@fi-shared/utils';
import { FiTaskStatusType } from './fi-task-v2-enums';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

@Injectable()
export class FiTaskV2HelperService {
    private _taskStatusChange = new Subject<{ task: ITaskV2; newStatus: FiTaskStatusType }>();

    constructor(
        private stateAccessCheckService: StateAccessCheckService,
        private appTextService: AppTextService,
        private userDataService: FiUserDataService,
        private fiSnackBarService: FiSnackBarService,
        private fiDialogsService: FiDialogsService,
        private fiStateService: FiStateService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    public get taskStatusChange$(): Observable<{ task: ITaskV2; newStatus: FiTaskStatusType }> {
        return this._taskStatusChange.asObservable();
    }

    getTaskV2SettingsTabs(): ITabItem[] {
        const tabs: ITabItem[] = [];

        if (this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.TaskV2.Current)) {
            tabs.push({
                Title: this.appTextService.get('tasks.current_tasks'),
                Sref: FiStateNames.TaskV2.Current,
                SrefParams: { filterType: SideListOptionType.TasksAssignedToMe },
                AddState: FiStateNames.TaskV2.Add,
            });

            tabs.push({
                Title: this.appTextService.get('task.completed_tasks'),
                Sref: FiStateNames.TaskV2.Completed,
                SrefParams: { filterType: SideListOptionType.TasksAssignedToMe },
            });

            tabs.push({
                Title: this.appTextService.get('task.cancelled_tasks'),
                Sref: FiStateNames.TaskV2.Cancelled,
                SrefParams: { filterType: SideListOptionType.TasksAssignedToMe },
            });

            tabs.push({
                Title: this.appTextService.get('task.archived_tasks'),
                Sref: FiStateNames.TaskV2.Archived,
                SrefParams: { filterType: SideListOptionType.SharedWithMe },
            });
        }

        if (this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.TaskV2.Report.Dashboard)) {
            tabs.push({
                Title: this.appTextService.get('reports'),
                Sref: FiStateNames.TaskV2.Report.Dashboard,
            });
        }

        return tabs;
    }

    getUpdatedTaskParameter(
        parameter: ITaskParameter,
        loadArgs: ILoadDataArgs,
        args: {
            filterType: SideListOptionType;
            isArchived: boolean;
            ids?: number[];
            taskStatuses?: FiTaskStatusType[];
        },
    ): ITaskParameter {
        return {
            ...parameter,
            FilterType: args.filterType,
            IsArchived: args.isArchived,
            Ids: args.ids ?? [],
            TaskStatuses: args.taskStatuses ?? [],
            FetchParams: {
                FetchItems: true,
                FetchTotalCount: false,
                FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
            },
        };
    }

    getTaskStatusFilterByViewMode(tasksViewMode: FiTasksViewMode): FiTaskStatusType[] | undefined {
        if (tasksViewMode === FiTasksViewMode.archived) {
            return [
                FiTaskStatusType.NotStarted,
                FiTaskStatusType.InProcess,
                FiTaskStatusType.Completed,
                FiTaskStatusType.Cancelled,
            ];
        }
        if (tasksViewMode === FiTasksViewMode.notCompleted) {
            return [FiTaskStatusType.NotStarted, FiTaskStatusType.InProcess];
        }
        if (tasksViewMode === FiTasksViewMode.completed) {
            return [FiTaskStatusType.Completed];
        }
        if (tasksViewMode === FiTasksViewMode.cancelled) {
            return [FiTaskStatusType.Cancelled];
        }
        return undefined;
    }

    getNewTaskV2Instance(args?: {
        userId?: number;
        taskType?: FiTaskType;
        checkpointAnswerId?: number;
        availableUserIds?: number[];
    }): ITaskV2 {
        if (!args) {
            args = {};
        }

        const currentUserId = this.userDataService.getUserId();
        const selectedUserId: number = args.userId || currentUserId;
        const isSelectedUserCurrent = selectedUserId === currentUserId;
        const hasAccessToSelectedUser: boolean =
            isSelectedUserCurrent ||
            (isDefined(args.availableUserIds) && args.availableUserIds.includes(selectedUserId));

        // https://chainformation.atlassian.net/browse/FI-6530?focusedCommentId=82085
        const userIds: number[] = [];
        let assignmentType: FiTaskAssignmentType;
        if (isSelectedUserCurrent || !this.hasTaskShareRights()) {
            assignmentType = FiTaskAssignmentType.Private;
            userIds.push(currentUserId);
        } else {
            assignmentType = FiTaskAssignmentType.Users;
            if (hasAccessToSelectedUser) {
                userIds.push(selectedUserId);
            }
        }

        const task: ITaskV2 = {
            Priority: FiTaskPriorityType.Medium,
            Status: FiTaskStatusType.NotStarted,
            TaskType: args.taskType ?? FiTaskType.Common,
            MediaBankFileReferences: [],
            AccessRightFilterCollection: AccessRightFilterCollectionModel.getNewInstance(),
            AssignmentType: assignmentType,
        };
        if (args.taskType === FiTaskType.ChecklistOneTime) {
            task.CheckpointAnswerId = args.checkpointAnswerId;
        }
        task.AccessRightFilterCollection.AccessRightUserFilter.UserIds.push(...userIds);
        return task;
    }

    hasTaskShareRights(): boolean {
        return this.accessToApplicationTypeService.HasAccessToApplicationType(
            ApplicationSettingsType.TaskV2Local,
            AccessRightType.Share,
        );
    }

    getPriorityTextByType(priorityType: FiTaskPriorityType): string {
        const priorityText = this.appTextService.get('common.priority').toLowerCase();
        let priorityTypeText: string;
        if (priorityType === FiTaskPriorityType.Low) {
            priorityTypeText = this.appTextService.get('common.low');
        } else if (priorityType === FiTaskPriorityType.Medium) {
            priorityTypeText = this.appTextService.get('common.medium');
        } else if (priorityType === FiTaskPriorityType.High) {
            priorityTypeText = this.appTextService.get('common.high');
        } else {
            throw new Error(`unknown priorityType ${priorityType} in getPriorityTextByType`);
        }

        return priorityTypeText + ' ' + priorityText;
    }

    /*isTaskPrivate(taskAccessRightFilterCollection: IAccessRightFilterCollection): boolean {
        // task has sharing only to current user
        const coreFilter = taskAccessRightFilterCollection.AccessRightCoreFilter;
        const userFilter = taskAccessRightFilterCollection.AccessRightUserFilter;
        return (
            !coreFilter.RoleIds.length &&
            !coreFilter.BusinessAreaIds.length &&
            !coreFilter.BusinessUnitIds.length &&
            !coreFilter.AreaIds.length &&
            !coreFilter.ClusterIds.length &&
            userFilter.UserIds.length === 1 &&
            this.isUserTaskCreator(userFilter.UserIds[0])
        );
    }*/

    getAssignmentType(
        taskAccessRightFilterCollection: IAccessRightFilterCollection,
        taskCreatedByUserId: number,
        initialAssignmentType: FiTaskAssignmentType,
    ): FiTaskAssignmentType {
        if (!isDefined(taskCreatedByUserId)) {
            taskCreatedByUserId = this.userDataService.getUserId();
        }
        const coreFilter = taskAccessRightFilterCollection.AccessRightCoreFilter;
        const userFilter = taskAccessRightFilterCollection.AccessRightUserFilter;
        if (coreFilter.RoleIds.length || coreFilter.BusinessUnitIds.length) {
            return FiTaskAssignmentType.RolesUnits;
        }
        if (userFilter.UserIds.find((userId) => userId !== taskCreatedByUserId)) {
            return FiTaskAssignmentType.Users;
        }
        if (userFilter.UserIds.length) {
            return FiTaskAssignmentType.Private;
        }
        // https://chainformation.atlassian.net/browse/FI-6766
        // assigned users or roles/units were deleted
        return initialAssignmentType;
    }

    isTaskToBeDone(task: ITaskV2Base) {
        const isToBeDoneStatus =
            task.Status === FiTaskStatusType.NotStarted || task.Status === FiTaskStatusType.InProcess;
        const isToBeDoneCategory =
            task.Categories.includes(FiTaskCategoryType.Private) ||
            task.Categories.includes(FiTaskCategoryType.AssignedToMe);
        return isToBeDoneStatus && !task.IsArchived && isToBeDoneCategory;
        // Assigned to me or Private tasks which not completed and not archived
    }

    hasEditAccess(task: ITaskV2): boolean {
        return this.isUserTaskCreator(task.CreatedByUserId);
    }

    isUserTaskCreator(taskCreatedByUserId: number): boolean {
        return this.userDataService.getUserId() === taskCreatedByUserId;
    }

    canAddAssignee(createdByUserId: number, isArchived: boolean): boolean {
        if (isArchived) {
            return false;
        }
        return this.isUserTaskCreator(createdByUserId) || this.hasUserAccessToAddAssignee();
    }

    public emitTaskStatusChange(task: ITaskV2, newStatus: FiTaskStatusType): void {
        this._taskStatusChange.next({ task, newStatus });
    }

    showTaskStatusChangeSnackbar(
        taskName: string | string[],
        newStatus: FiTaskStatusType,
        closeButtonTextId = 'common.undo',
    ): Observable<void> {
        const snackbarMessage = Array.isArray(taskName)
            ? this.getMultipleTaskNewStatusSnackbarText(taskName.length, newStatus)
            : this.getTaskNewStatusSnackbarText(taskName, newStatus);
        return this.getStatusChangedSnackbar(snackbarMessage, this.appTextService.get(closeButtonTextId)).onAction();
    }

    showTaskStatusChangeUndoneSnackbar(taskName: string | string[], prevStatus: FiTaskStatusType): void {
        const undoSnackbarMessage = Array.isArray(taskName)
            ? this.getMultipleTaskUndoStatusChangeSnackbarText(taskName.length, prevStatus)
            : this.getTaskUndoStatusChangeSnackbarText(taskName, prevStatus);
        this.getStatusChangedSnackbar(undoSnackbarMessage, this.appTextService.get('common.dismiss'));
    }

    showTaskStatusChangeSnackbarReport(
        taskName: string,
        newStatus: FiTaskStatusType,
        closeButtonTextId = 'common.undo',
    ): Observable<void> {
        const snackbarMessage = this.getTaskNewStatusSnackbarTextReport(taskName, newStatus);
        return this.getStatusChangedSnackbar(snackbarMessage, this.appTextService.get(closeButtonTextId)).onAction();
    }

    showTaskStatusChangeUndoneSnackbarReport(taskName: string, prevStatus: FiTaskStatusType): void {
        const undoSnackbarMessage = this.getTaskUndoStatusChangeSnackbarTextReport(taskName, prevStatus);
        this.getStatusChangedSnackbar(undoSnackbarMessage, this.appTextService.get('common.dismiss'));
    }

    getStatusChangedSnackbar(snackbarMessage: string, buttonText: string) {
        return this.fiSnackBarService.showSnackbar(
            {
                textMessage: snackbarMessage,
                buttonText: buttonText,
                isSnackbarBorderShown: true,
                preventButtonUppercase: true,
                buttonClass: 'BrandedColor',
            },
            false,
            'fi-snackbar-standard',
        );
    }

    showReminderSentSnackbar(): void {
        this.fiSnackBarService.showSnackbar(
            {
                textMessage: this.appTextService.get('taskv2.reminder_sent'),
                buttonText: this.appTextService.get('common.dismiss'),
                isSnackbarBorderShown: true,
                preventButtonUppercase: true,
                buttonClass: 'BrandedColor',
            },
            false,
            'fi-snackbar-standard',
        );
    }

    showTaskDeletedSnackbar(taskName: string, tasksDeletedCount = 1): void {
        this.fiSnackBarService.showSnackbar(
            {
                iconClass: 'fas fa-circle-info',
                textMessage:
                    tasksDeletedCount === 1
                        ? this.appTextService.get('task.task_deleted_snackbar', {
                              '{0}': taskName,
                          })
                        : this.appTextService.get('task.task_deleted_multiple_snackbar', {
                              '{0}': tasksDeletedCount,
                          }),
                buttonText: this.appTextService.get('common.dismiss'),
                isSnackbarBorderShown: true,
                preventButtonUppercase: true,
                colorClass: 'red',
            },
            false,
            'fi-snackbar-standard',
        );
    }

    public getTaskNewStatusSnackbarText(taskName: string, newStatus: FiTaskStatusType): string {
        switch (newStatus) {
            case FiTaskStatusType.Completed:
                return this.appTextService.get('taskv2.moved_to_completed', { '{0}': taskName });
            case FiTaskStatusType.Cancelled:
                return this.appTextService.get('taskv2.moved_to_cancelled', { '{0}': taskName });
            case FiTaskStatusType.NotStarted:
            case FiTaskStatusType.InProcess:
                return this.appTextService.get('taskv2.moved_to_not_started', { '{0}': taskName });
            default:
                return '';
        }
    }

    public getTaskNewStatusSnackbarTextReport(taskName: string, newStatus: FiTaskStatusType): string {
        switch (newStatus) {
            case FiTaskStatusType.Completed:
                return this.appTextService.get('taskv2.moved_to_completed', { '{0}': taskName });
            case FiTaskStatusType.Cancelled:
                return this.appTextService.get('taskv2.moved_to_cancelled', { '{0}': taskName });
            case FiTaskStatusType.NotStarted:
                return this.appTextService.get('taskv2.moved_to_not_started_report', { '{0}': taskName });
            case FiTaskStatusType.InProcess:
                return this.appTextService.get('taskv2.moved_to_in_progress_report', { '{0}': taskName });
            default:
                return '';
        }
    }

    public getTaskUndoStatusChangeSnackbarTextReport(taskName: string, prevStatus: FiTaskStatusType): string {
        switch (prevStatus) {
            case FiTaskStatusType.Completed:
                return this.appTextService.get('taskv2.moved_to_completed_undo', { '{0}': taskName });
            case FiTaskStatusType.Cancelled:
                return this.appTextService.get('taskv2.moved_to_cancelled_undo', { '{0}': taskName });
            case FiTaskStatusType.NotStarted:
                return this.appTextService.get('taskv2.moved_to_not_started_undo_report', { '{0}': taskName });
            case FiTaskStatusType.InProcess:
                return this.appTextService.get('taskv2.moved_to_in_progress_undo_report', { '{0}': taskName });
            default:
                return '';
        }
    }

    private getMultipleTaskNewStatusSnackbarText(taskCount: number, newStatus: FiTaskStatusType): string {
        switch (newStatus) {
            case FiTaskStatusType.Completed:
                return this.appTextService.get('tasks.moved_to_completed_multiple', { '{0}': taskCount });
            default:
                return '';
        }
    }

    public getTaskUndoStatusChangeSnackbarText(taskName: string, prevStatus: FiTaskStatusType): string {
        switch (prevStatus) {
            case FiTaskStatusType.Completed:
                return this.appTextService.get('taskv2.moved_to_completed_undo', { '{0}': taskName });
            case FiTaskStatusType.Cancelled:
                return this.appTextService.get('taskv2.moved_to_cancelled_undo', { '{0}': taskName });
            case FiTaskStatusType.NotStarted:
            case FiTaskStatusType.InProcess:
                return this.appTextService.get('taskv2.moved_to_not_started_undo', { '{0}': taskName });
            default:
                return '';
        }
    }

    private getMultipleTaskUndoStatusChangeSnackbarText(taskCount: number, prevStatus: FiTaskStatusType): string {
        switch (prevStatus) {
            case FiTaskStatusType.NotStarted:
            case FiTaskStatusType.InProcess:
                return this.appTextService.get('taskv2.moved_to_current_multiple_undo', { '{0}': taskCount });
            default:
                return '';
        }
    }

    getTaskPriorityText(priority: FiTaskPriorityType): string {
        const textId = FiTaskPriorities.find((item) => item.id === priority)?.textId;
        if (textId) {
            return this.appTextService.get(textId);
        }
        throw new Error(`unknown task priority ${priority} at getTaskPriorityText`);
    }

    getTaskCategoriesText(categories: FiTaskCategoryType[]): string {
        return categories.map((category) => this.getTaskCategoryText(category)).join('\n');
    }

    getTaskCategoryText(category: FiTaskCategoryType): string {
        const textId = FiTaskCategories.find((item) => item.id === category)?.textId;
        if (textId) {
            return this.appTextService.get(textId);
        }
        throw new Error(`unknown task category ${category} at getTaskCategoryText`);
    }

    isCancelledOptionHidden(
        viewMode: FiTasksViewMode,
        createdByUserId: number,
        currentTaskStatus: FiTaskStatusType,
    ): boolean {
        return (
            currentTaskStatus !== FiTaskStatusType.Cancelled &&
            viewMode !== FiTasksViewMode.cancelled &&
            !this.isUserTaskCreator(createdByUserId)
        );
    }

    isCancelledOptionDisabled(viewMode: FiTasksViewMode, createdByUserId: number): boolean {
        return /*viewMode === FiTasksViewMode.cancelled && */ !this.isUserTaskCreator(createdByUserId);
    }

    canShowTaskSnackbarOnStatusChange(prevStatus: FiTaskStatusType, newStatus: FiTaskStatusType): boolean {
        const isStartTransition =
            prevStatus === FiTaskStatusType.NotStarted && newStatus === FiTaskStatusType.InProcess;
        const isRevertStartTransition =
            prevStatus === FiTaskStatusType.InProcess && newStatus === FiTaskStatusType.NotStarted;

        return !(isStartTransition || isRevertStartTransition);
    }

    getSaveChangesWarningData(isEditMode: boolean) {
        let headerText: string;
        let messageText: string;
        let yesButtonText: string;
        if (isEditMode) {
            headerText = this.appTextService.get('common.discard_changes_question');
            messageText = this.appTextService.get('common.leave_page_without_saving_warning_message');
            yesButtonText = this.appTextService.get('common.discard_changes');
        } else {
            headerText = this.appTextService.get('warning.exit_without_saving_question');
            messageText = this.appTextService.get('task.leave_without_saving_warning');
            yesButtonText = this.appTextService.get('warning.exit_without_saving');
        }
        return {
            headerText,
            messageText,
            yesButtonText,
            config: {
                maxWidth: '560px',
            },
        };
    }

    getEditorHeaderText(isEditMode: boolean, taskName: string): string {
        return isEditMode
            ? this.appTextService.get('task.edit_task') + ': ' + taskName
            : this.appTextService.get('task.new_task');
    }

    showTaskSavedSnackbar(): void {
        this.fiSnackBarService.showSnackbar(
            {
                textMessage: this.appTextService.get('task.task_saved'),
                buttonText: this.appTextService.get('common.dissmiss'),
                isSnackbarBorderShown: true,
            },
            false,
            'fi-snackbar-standard',
        );
    }

    showTaskRestoredSnackbar(
        taskName: string,
        args?: {
            tasksRestoredCount: number;
            isUndoAvailable?: boolean;
            isUndoMode?: boolean;
            taskStatus?: FiTaskStatusType;
        },
    ): Observable<void> {
        if (!args) {
            args = {
                tasksRestoredCount: 1,
            };
        }
        const buttonTextId = args.isUndoAvailable ? 'common.undo' : 'common.dismiss';
        let messageText = '';
        if (!args.isUndoMode) {
            messageText =
                args.tasksRestoredCount === 1
                    ? this.appTextService.get('task.task_restored', { '{0}': taskName })
                    : this.appTextService.get('task.tasks_restored', { '{0}': args.tasksRestoredCount });
        } else {
            messageText = this.getTaskUndoStatusChangeSnackbarText(taskName, args.taskStatus);
        }

        return this.fiSnackBarService
            .showSnackbar(
                {
                    iconClass: 'fas fa-circle-info',
                    textMessage: messageText,
                    buttonText: this.appTextService.get(buttonTextId),
                    isSnackbarBorderShown: true,
                    preventButtonUppercase: true,
                    colorClass: 'blue',
                },
                false,
                'fi-snackbar-standard',
            )
            .onAction();
    }

    showAllItemsMandatoryErrorDialog(): void {
        this.fiDialogsService.notify({
            messageText: this.appTextService.get('common.error_all_mandatory'),
            headerText: this.appTextService.get('common.warning'),
            config: {
                panelClass: ['fi-rounded-dialog-panel'],
                autoFocus: false,
                restoreFocus: false,
            },
        });
    }

    showArchiveTaskConfirmDialog(): IFiDialogResult<unknown> {
        return this.fiDialogsService.confirm({
            colorClass: 'orange',
            headerIconClass: 'far fa-circle-exclamation orange',
            headerText: this.appTextService.get('task.archive_task'),
            messageText: this.appTextService.get('task.archive_task_confirm_message'),
            yesButtonText: this.appTextService.get('common.archive'),
            noButtonText: this.appTextService.get('common.cancel'),
            isBigSizeTopBorder: true,
            preventUppercaseButtonLabel: true,
            config: {
                panelClass: ['fi-rounded-dialog-panel'],
                maxWidth: '560px',
                autoFocus: false,
                restoreFocus: false,
            },
        });
    }

    showTaskArchivedSnackbar(taskName: string): Observable<void> {
        return this.fiSnackBarService
            .showSnackbar(
                {
                    iconClass: 'fas fa-circle-info',
                    textMessage: this.appTextService.get('task.task_archived', { '{0}': taskName }),
                    //buttonText: this.appTextService.get('common.dismiss'),
                    //durationMs: 15000,
                    buttonText: this.appTextService.get('common.undo'),
                    isSnackbarBorderShown: true,
                    preventButtonUppercase: true,
                    colorClass: 'blue',
                },
                false,
                'fi-snackbar-standard',
            )
            .onAction();
    }

    showDeleteTaskConfirmDialog(tasksCount = 1): IFiDialogResult<unknown> {
        let headerText: string;
        let messageText: string;
        if (tasksCount === 1) {
            headerText = this.appTextService.get('task.delete_task');
            messageText = this.appTextService.get('task.delete_task_confirm_message');
        } else {
            headerText = this.appTextService.get('task.delete_tasks');
            messageText = this.appTextService.get('task.delete_multiple_task_confirm_message', { '{0}': tasksCount });
        }
        return this.fiDialogsService.confirm({
            colorClass: 'red',
            headerIconClass: 'far fa-trash red',
            headerText,
            messageText,
            yesButtonText: this.appTextService.get('common.delete'),
            noButtonText: this.appTextService.get('common.cancel'),
            isBigSizeTopBorder: true,
            preventUppercaseButtonLabel: true,
            config: {
                panelClass: ['fi-rounded-dialog-panel'],
                maxWidth: '560px',
                autoFocus: false,
                restoreFocus: false,
            },
        });
    }

    private hasUserAccessToAddAssignee(): boolean {
        return (
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.TaskV2Local,
                AccessRightType.Share,
            ) ||
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.TaskV2Central,
                AccessRightType.Share,
            ) ||
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.TaskV2Global,
                AccessRightType.Share,
            )
        );
    }

    canOpenTaskChecklistView(checklistData: ITaskV2ChecklistDataBase): boolean {
        return !!checklistData?.ChecklistToScheduleId && !!checklistData?.HasViewAccessToChecklist;
    }

    openTaskChecklistView(checklistData: ITaskV2ChecklistDataBase) {
        const rootState = FiStateNames.getChecklistV2RootState(checklistData.IsChecklistAudit);
        this.fiStateService.go(rootState.View, {
            id: checklistData.ChecklistToScheduleId,
            businessUnitId: checklistData.BusinessUnitId,
            checklistPeriodId: checklistData.ChecklistPeriodId,
            isPreviewMode: true,
        });
    }

    getNormalizedTaskV2(): boolean {
        const result = this.accessToApplicationTypeService.HasAccessToApplicationType(
            ApplicationSettingsType.TaskV2Local,
            AccessRightType.Read,
        );
        return result;
    }

    getCreatedLabel(date: string) {
        const createdOnText = this.fiFormatDateService.formatDate(date, FiDateTimeFormatOptions, {
            //showToday: false,
        });
        return `${this.appTextService.get('common.created')}: ${createdOnText}`;
    }

    getCompletedLabel(date: string) {
        const createdOnText = this.fiFormatDateService.formatDate(date, FiDateTimeFormatOptions, {
            //showToday: false,
        });
        return `${this.appTextService.get('common.completed')}: ${createdOnText}`;
    }
}
